import { useCallback } from "react";

import type { CustomTypeRoomTitle } from "@/lib/gql/__generated__/graphql";

import { useHotelInfoAddress } from "./use-address";
import { useHotelInfoKeyVisual } from "./use-key-visual";
import { useHotelInfoLogo } from "./use-logo";
import { useHotelInfoSNS } from "./use-sns";

import type { HotelSettingsHotelInfoFormModelOnSetViewModelType } from "..";
import type { HotelSettingsHotelInfoFormModelAddressAndGeoLocationMethodsType } from "./use-address";
import type { HotelSettingsHotelInfoFormModelKeyVisualMethodsType } from "./use-key-visual";
import type { HotelSettingsHotelInfoFormModelLogoImageMethodsType } from "./use-logo";
import type { HotelSettingsHotelInfoFormModelSNSMethodsType } from "./use-sns";
import type { HotelSettingsHotelInfoFormModel } from "../../../type";

type Props = {
	viewModel: HotelSettingsHotelInfoFormModel["ViewModel"];
	onSetViewModel: HotelSettingsHotelInfoFormModelOnSetViewModelType;
};

export type HotelSettingsHotelInfoFormModelMethodsType = {
	hotelSettingsHotelInfoFormModelMethods: {
		onSetName: (name: string) => void;
		onSetCatchCopy: (name: string) => void;
		onSetEmailAddress: (name: string) => void;
		onSetTel: (name: string) => void;
		onSetCustomTypeRoomTitle: (name: string) => void;
	};
} & HotelSettingsHotelInfoFormModelLogoImageMethodsType &
	HotelSettingsHotelInfoFormModelKeyVisualMethodsType &
	HotelSettingsHotelInfoFormModelAddressAndGeoLocationMethodsType &
	HotelSettingsHotelInfoFormModelSNSMethodsType;

export const useHotelSettingsHotelInfoFormModelMethods = ({
	viewModel,
	onSetViewModel,
}: Props): HotelSettingsHotelInfoFormModelMethodsType => {
	const onSetName = useCallback(
		(name: string) => {
			onSetViewModel([
				{
					key: "name",
					value: {
						isEdited: true,
						value: name,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetCatchCopy = useCallback(
		(catchCopy: string) => {
			onSetViewModel([
				{
					key: "catchCopy",
					value: {
						isEdited: true,
						value: catchCopy,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetEmailAddress = useCallback(
		(email: string) => {
			onSetViewModel([
				{
					key: "email",
					value: {
						isEdited: true,
						value: email,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetTel = useCallback(
		(telNumber: string) => {
			onSetViewModel([
				{
					key: "tel",
					value: {
						isEdited: true,
						value: telNumber,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetCustomTypeRoomTitle = useCallback(
		(input: string) => {
			onSetViewModel([
				{
					key: "customTypeRoomTitle",
					value: {
						isEdited: true,
						value: input as CustomTypeRoomTitle,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	return {
		hotelSettingsHotelInfoFormModelMethods: {
			onSetName,
			onSetCatchCopy,

			onSetEmailAddress,
			onSetTel,

			onSetCustomTypeRoomTitle,
		},

		...useHotelInfoLogo({ viewModel, onSetViewModel }),
		...useHotelInfoKeyVisual({ viewModel, onSetViewModel }),
		...useHotelInfoAddress({ viewModel, onSetViewModel }),
		...useHotelInfoSNS({ viewModel, onSetViewModel }),
	};
};
